import type { ToolCallState } from "../../domain/toolCallMachine";
import { FindingsList } from "../FindingsList/FindingsList";
import { ErrorSummary } from "../ErrorSummary/ErrorSummary";
import { ExportPanel } from "../ExportPanel/ExportPanel";
import { EmptyState } from "../EmptyState/EmptyState";
import "./ToolResultPanel.css";

export interface ToolResultPanelProps {
  state: ToolCallState;
  onRetry?: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  completed: "Completado",
  partial: "Parcial",
  failed: "Fallido",
  cancelled: "Cancelado",
};

/**
 * Panel de resultado: estado, explicación, hallazgos, evidencia y exportación
 * (P3-R1). Cubre además los estados vacío y de error (P3-R3) con el mismo componente,
 * en vez de tener rutas visuales distintas y difíciles de mantener sincronizadas.
 */
export function ToolResultPanel({ state, onRetry }: ToolResultPanelProps) {
  if (state.status === "idle" || state.status === "permission-denied") {
    return (
      <EmptyState
        title="Aún no hay resultado."
        description={
          state.status === "permission-denied"
            ? "Denegaste el permiso; la herramienta no se ejecutó. Puedes solicitarlo de nuevo."
            : "Ejecuta el fixture para ver cada decisión."
        }
      />
    );
  }

  if (state.status === "awaiting-permission" || state.status === "awaiting-confirmation") {
    return (
      <EmptyState
        title="Esperando tu decisión."
        description="Responde al permiso o a la confirmación para continuar la ejecución."
      />
    );
  }

  if (state.status === "running") {
    return (
      <div className="tool-result-panel__running" aria-live="polite">
        <p>Procesando la herramienta…</p>
        <progress value={state.progress} max={100}>
          {state.progress}%
        </progress>
        <p className="mono">{state.progress}%</p>
      </div>
    );
  }

  if (state.status === "cancelled") {
    return (
      <EmptyState
        title="Ejecución cancelada."
        description="No se procesó ningún resultado. Puedes reiniciar el escenario cuando quieras."
      />
    );
  }

  if (state.status === "error" && state.error) {
    return <ErrorSummary error={state.error} onRetry={onRetry} />;
  }

  if (state.status === "completed" && state.result) {
    const { result } = state;
    return (
      <div className="tool-result-panel">
        <p className={`badge badge--${result.status === "partial" ? "warning" : "success"}`}>
          {STATUS_LABEL[result.status] ?? result.status}
        </p>
        <p className="tool-result-panel__summary">{result.summary}</p>
        <h4>Hallazgos</h4>
        <FindingsList findings={result.findings} />
        <dl className="tool-result-panel__evidence">
          <div>
            <dt>Ejecución</dt>
            <dd className="mono">{result.runId}</dd>
          </div>
          <div>
            <dt>Versión de reglas</dt>
            <dd className="mono">{result.evidence.rulesVersion}</dd>
          </div>
        </dl>
        <ExportPanel output={result} />
      </div>
    );
  }

  return null;
}
